import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import WingitPublicPage from './components/WingitPublicPage';
import WingitPanel from './components/WingitPanel';
import WingitButton from './components/WingitButton';
import WingitInput from './components/WingitInput';
import WingitField from './components/WingitField';
import WingitLogo from './components/WingitLogo';
import wingitDesign from './designSystem';

function getResetErrorMessage(error) {
  const rawMessage = String(error?.message || '').trim();
  const lowerMessage = rawMessage.toLowerCase();

  if (
    lowerMessage.includes('expired') ||
    lowerMessage.includes('invalid') ||
    lowerMessage.includes('auth session missing') ||
    lowerMessage.includes('refresh token not found') ||
    lowerMessage.includes('jwt')
  ) {
    return 'This password reset link is invalid or expired. Please request another reset email.';
  }

  return rawMessage || 'Could not update password.';
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const redirectTimerRef = useRef(null);

  const [form, setForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  function handleChange(event) {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setError('');
    setStatus('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const newPassword = form.newPassword;
    const confirmPassword = form.confirmPassword;

    setError('');
    setStatus('');

    if (!newPassword || !confirmPassword) {
      setError('Please complete both password fields.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSaving(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      setStatus('Password updated successfully.');
      setForm({
        newPassword: '',
        confirmPassword: '',
      });

      redirectTimerRef.current = setTimeout(() => {
        navigate('/login');
      }, 1600);
    } catch (err) {
      setError(getResetErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <WingitPublicPage
      rightActionLabel="Log in"
      onRightAction={() => navigate('/login')}
    >
      <main style={styles.hero}>
        <div style={styles.logoWrap}>
          <WingitLogo size="small" />
        </div>

        <h1 style={styles.title}>Choose a new password</h1>

        <WingitPanel>
          <form onSubmit={handleSubmit} style={styles.form}>
            <WingitField label="New Password">
              <WingitInput
                name="newPassword"
                type="password"
                value={form.newPassword}
                onChange={handleChange}
                autoComplete="new-password"
                placeholder="Enter new password"
                disabled={saving}
              />
            </WingitField>

            <WingitField label="Confirm Password">
              <WingitInput
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                placeholder="Confirm new password"
                disabled={saving}
              />
            </WingitField>

            {error && <div style={styles.error}>{error}</div>}
            {status && <div style={styles.success}>{status}</div>}

            <WingitButton type="submit" disabled={saving}>
              {saving ? 'Updating...' : 'Update Password'}
            </WingitButton>
          </form>
        </WingitPanel>
      </main>
    </WingitPublicPage>
  );
}

const styles = {
  hero: {
    ...wingitDesign.publicPages.hero,
    maxWidth: 560,
  },

  logoWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.4rem',
  },

  title: wingitDesign.publicPages.title,

  form: {
    display: 'grid',
    gap: '0.9rem',
  },

  error: {
    color: '#ffb3b3',
    background: 'rgba(255,77,77,0.08)',
    border: '1px solid rgba(255,77,77,0.18)',
    borderRadius: 10,
    padding: '0.75rem',
  },

  success: {
    color: '#b7ffdc',
    background: 'rgba(47,211,125,0.08)',
    border: '1px solid rgba(47,211,125,0.24)',
    borderRadius: 10,
    padding: '0.75rem',
  },
};